// components/views/profile/ProfilePage.jsx
import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { HTTPService } from '../../../services/httpService';
import { toast } from 'react-toastify';
import PaymentSetup from './PaymentSetup';
import LocationService, { getDefaultOperatingHours, formatTime } from '../../../services/locationService';

const API = new HTTPService();

// Time tracking mode options
const TIME_TRACKING_OPTIONS = [
  { value: 'none', label: 'Disabled', description: 'Time tracking features are hidden' },
  { value: 'voluntary', label: 'Voluntary', description: 'Staff can optionally check in/out' },
  { value: 'required', label: 'Required', description: 'Staff must check in before starting work' }
];

class ProfilePage extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      user: this.props.userId,
      installer: this.props.entityId,
      loading: true,
      error: null,
      onboardingStep: 'overview', // overview, payment, complete
      paymentSetupStatus: 'pending', // pending, in_progress, complete

      // Location Settings Modal
      showSettingsModal: false,
      settingsLoading: false,
      settingsForm: {
        materials_tracking_enabled: true,
        time_tracking_mode: 'none'
      },

      // Calendar Connection
      isCalendarConnected: false,
      calendarLoading: false,
      showCalendarSelection: false,
      availableCalendars: [],

      // Payment
      paymentLoading: false,

      // Operating Hours
      operatingHours: [],
      operatingHoursLoading: false,
      showOperatingHoursModal: false,
      operatingHoursForm: []
    };
  }

  async componentDidMount() {
    await this.loadUserProfile();
    this.checkOnboardingStatus();
    await this.checkCalendarConnection();
    await this.loadOperatingHours();
    this.handleOAuthRedirect();
  }

  loadUserProfile = async () => {
    try {
      // Get user and installer information
      const userResponse = await API.get(`/profile/${this.state.user}`);
      const installerResponse = await API.get(`/installers/${this.state.installer}`);

      // Fetch Stripe capabilities if account is fully set up
      let stripeCapabilities = {};
      if (installerResponse.data[0]?.stripe_account_id && installerResponse.data[0]?.onboarding_complete) {
        try {
          const capResponse = await API.get(`/api/installers/${this.state.installer}/stripe-status`);
          stripeCapabilities = capResponse.data || {};
        } catch (e) {
          console.error('Error loading Stripe capabilities:', e);
        }
      }

      this.setState({
        user: userResponse.data[0],
        installer: installerResponse.data[0],
        stripeCapabilities,
        loading: false
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      this.setState({
        error: 'Failed to load profile information',
        loading: false
      });
    }
  };

  checkOnboardingStatus = () => {
    const { installer } = this.state;
    
    if (!installer) return;

    // Determine current onboarding step based on installer status
    if (installer.stripe_account_id && installer.onboarding_complete) {
      this.setState({ 
        onboardingStep: 'complete',
        paymentSetupStatus: 'complete'
      });
    } else if (installer.stripe_account_id && !installer.onboarding_complete) {
      this.setState({ 
        onboardingStep: 'payment',
        paymentSetupStatus: 'in_progress'
      });
    } else {
      this.setState({ 
        onboardingStep: 'payment',
        paymentSetupStatus: 'pending'
      });
    }
  };

  handlePaymentSetupComplete = () => {
    this.setState({
      onboardingStep: 'complete',
      paymentSetupStatus: 'complete'
    });
    this.loadUserProfile(); // Refresh data
  };

  // Open Stripe account management
  handleManagePayment = async () => {
    try {
      this.setState({ paymentLoading: true });
      const response = await API.post('/stripe/continue-onboarding', {
        installerId: this.state.installer.Installer_id
      });

      if (response.data.accountLinkUrl) {
        window.location.href = response.data.accountLinkUrl;
      }
    } catch (error) {
      console.error('Error opening payment management:', error);
      toast.error('Failed to open payment management. Please try again.');
      this.setState({ paymentLoading: false });
    }
  };

  // Open settings modal with current values
  handleOpenSettingsModal = () => {
    const { installer } = this.state;
    this.setState({
      showSettingsModal: true,
      settingsForm: {
        materials_tracking_enabled: installer?.materials_tracking_enabled !== false,
        time_tracking_mode: installer?.time_tracking_mode || 'none'
      }
    });
  };

  // Close settings modal
  handleCloseSettingsModal = () => {
    this.setState({ showSettingsModal: false });
  };

  // Handle form field changes
  handleSettingsChange = (field, value) => {
    this.setState(prevState => ({
      settingsForm: {
        ...prevState.settingsForm,
        [field]: value
      }
    }));
  };

  // Save settings
  handleSaveSettings = async () => {
    const { installer, settingsForm } = this.state;

    try {
      this.setState({ settingsLoading: true });

      await API.put(`/api/installer-location/${installer.Installer_id}/time-settings`, {
        materials_tracking_enabled: settingsForm.materials_tracking_enabled,
        time_tracking_mode: settingsForm.time_tracking_mode,
        actor_user_id: this.props.userId
      });

      // Update local state
      this.setState(prevState => ({
        installer: {
          ...prevState.installer,
          materials_tracking_enabled: settingsForm.materials_tracking_enabled,
          time_tracking_mode: settingsForm.time_tracking_mode
        },
        showSettingsModal: false,
        settingsLoading: false
      }));

      // Update localStorage so other components pick up the change
      const companyData = JSON.parse(localStorage.getItem('companyAPI') || '{}');
      companyData.materials_tracking_enabled = settingsForm.materials_tracking_enabled;
      companyData.time_tracking_mode = settingsForm.time_tracking_mode;
      localStorage.setItem('companyAPI', JSON.stringify(companyData));

      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings. Please try again.');
      this.setState({ settingsLoading: false });
    }
  };

  // Get display label for time tracking mode
  getTimeTrackingLabel = (mode) => {
    const option = TIME_TRACKING_OPTIONS.find(o => o.value === mode);
    return option ? option.label : 'Disabled';
  };

  // Check calendar connection status
  checkCalendarConnection = async () => {
    try {
      const response = await API.get(`/installerAuth/${this.props.entityId}`);
      if (response.data && response.data[0]) {
        const { authenticated } = response.data[0];
        this.setState({ isCalendarConnected: authenticated });
      }
    } catch (error) {
      console.error('Error checking calendar connection:', error);
    }
  };

  // Start OAuth flow to connect calendar
  connectCalendar = async () => {
    try {
      this.setState({ calendarLoading: true });
      const { data: authUrl } = await API.get(`/googlecal/auth/${this.props.entityId}`);
      window.location.href = authUrl;
    } catch (error) {
      console.error('Calendar connection error:', error);
      toast.error('Failed to connect calendar. Please try again.');
      this.setState({ calendarLoading: false });
    }
  };

  // Handle OAuth redirect after Google auth
  handleOAuthRedirect = () => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('calendar_selection')) {
      try {
        const calendarsParam = urlParams.get('calendars');
        const installerId = urlParams.get('installer_id');
        const calendars = JSON.parse(calendarsParam);

        if (installerId === this.props.entityId.toString()) {
          this.setState({
            showCalendarSelection: true,
            availableCalendars: calendars
          });
        }

        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (e) {
        console.error('Error parsing calendar list:', e);
        toast.error('Error loading calendar options. Please try again.');
      }
    }
  };

  // Handle calendar selection from modal
  handleCalendarSelection = async (selectedCalendar) => {
    try {
      await API.post('/googlecal/select', {
        installerId: this.props.entityId,
        calendarId: selectedCalendar.id
      });

      this.setState({
        showCalendarSelection: false,
        availableCalendars: [],
        isCalendarConnected: true
      });

      // Notify other components
      window.dispatchEvent(new CustomEvent('calendarStatusChanged', { detail: { connected: true } }));

      toast.success(`Calendar "${selectedCalendar.name}" connected successfully!`);
    } catch (error) {
      console.error('Error saving calendar selection:', error);
      toast.error('Failed to save calendar selection. Please try again.');
    }
  };

  // Close calendar selection modal
  handleCloseCalendarSelection = () => {
    this.setState({
      showCalendarSelection: false,
      availableCalendars: []
    });
  };

  // Disconnect calendar
  disconnectCalendar = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Google Calendar?')) {
      return;
    }

    try {
      this.setState({ calendarLoading: true });
      await API.delete(`/googlecal/disconnect/${this.props.entityId}`);

      this.setState({
        isCalendarConnected: false,
        calendarLoading: false
      });

      // Notify other components
      window.dispatchEvent(new CustomEvent('calendarStatusChanged', { detail: { connected: false } }));

      toast.success('Calendar disconnected successfully.');
    } catch (error) {
      console.error('Calendar disconnection error:', error);
      toast.error('Failed to disconnect calendar. Please try again.');
      this.setState({ calendarLoading: false });
    }
  };

  // Operating Hours Methods
  loadOperatingHours = async () => {
    try {
      this.setState({ operatingHoursLoading: true });
      const data = await LocationService.getOperatingHours(this.props.entityId);
      const hours = data.hours || getDefaultOperatingHours();
      this.setState({
        operatingHours: hours,
        operatingHoursLoading: false
      });
    } catch (error) {
      console.error('Error loading operating hours:', error);
      // Fall back to defaults on error
      this.setState({
        operatingHours: getDefaultOperatingHours(),
        operatingHoursLoading: false
      });
    }
  };

  handleOpenOperatingHoursModal = () => {
    // Copy current hours to form for editing
    const formHours = this.state.operatingHours.length > 0
      ? JSON.parse(JSON.stringify(this.state.operatingHours))
      : getDefaultOperatingHours();
    this.setState({
      showOperatingHoursModal: true,
      operatingHoursForm: formHours
    });
  };

  handleCloseOperatingHoursModal = () => {
    this.setState({ showOperatingHoursModal: false });
  };

  handleOperatingHoursChange = (dayIndex, field, value) => {
    this.setState(prevState => {
      const newForm = [...prevState.operatingHoursForm];
      newForm[dayIndex] = {
        ...newForm[dayIndex],
        [field]: value
      };
      // If closing, clear times
      if (field === 'is_open' && !value) {
        newForm[dayIndex].open_time = null;
        newForm[dayIndex].close_time = null;
      }
      // If opening and no times set, use defaults
      if (field === 'is_open' && value && !newForm[dayIndex].open_time) {
        newForm[dayIndex].open_time = '08:00';
        newForm[dayIndex].close_time = '17:00';
      }
      return { operatingHoursForm: newForm };
    });
  };

  handleSaveOperatingHours = async () => {
    try {
      this.setState({ operatingHoursLoading: true });
      await LocationService.saveOperatingHours(this.props.entityId, this.state.operatingHoursForm);
      this.setState({
        operatingHours: this.state.operatingHoursForm,
        showOperatingHoursModal: false,
        operatingHoursLoading: false
      });
      toast.success('Operating hours saved successfully');
    } catch (error) {
      console.error('Error saving operating hours:', error);
      toast.error('Failed to save operating hours. Please try again.');
      this.setState({ operatingHoursLoading: false });
    }
  };

  render() {
    const { installer, loading, error, onboardingStep, paymentSetupStatus, showSettingsModal, settingsForm, settingsLoading, isCalendarConnected, calendarLoading, showCalendarSelection, availableCalendars, operatingHours, operatingHoursLoading, showOperatingHoursModal, operatingHoursForm } = this.state;

    if (loading) {
      return (
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="mt-3">Loading profile...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="alert alert-danger">
                <h4>Error Loading Profile</h4>
                <p>{error}</p>
                <button className="btn btn-outline-danger" onClick={this.loadUserProfile}>
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-header">
              <h1>Profile Settings</h1>
              <p className="text-muted">Manage your account and business information</p>
            </div>
          </div>
        </div>

        {/* Estimate Page Banner */}
        <div className="row mb-4">
          <div className="col-12">
            <a
              href={`/estimate?installer=${this.props.entityId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="d-block text-decoration-none"
            >
              <div
                className="alert alert-success mb-0 d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
              >
                <div>
                  <strong><i className="fa fa-external-link-alt mr-2"></i>Preview Your Estimate Page</strong>
                  <span className="ml-2 text-dark">See what your customers experience when requesting an estimate</span>
                </div>
                <span className="badge badge-light">
                  Click to open <i className="fa fa-arrow-right ml-1"></i>
                </span>
              </div>
            </a>
          </div>
        </div>

        <div className="row">
          {/* Left Column - Main Content */}
          <div className="col-md-8">
            
            {/* Trial Specialist Onboarding Section - hide when complete */}
            {!!installer.is_trial && onboardingStep !== 'complete' && (
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h4 className="mb-0">Complete Your Account Setup</h4>
                </div>
                <div className="card-body">
                  
                  {/* Onboarding Progress */}
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="font-weight-bold">Setup Progress</span>
                      <span className="text-muted">
                        {onboardingStep === 'complete' ? '100%' : '50%'}
                      </span>
                    </div>
                    <div className="progress">
                      <div 
                        className="progress-bar bg-success" 
                        style={{
                          width: onboardingStep === 'complete' ? '100%' : '50%'
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Onboarding Steps */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <div className="mr-3">
                          <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '30px', height: '30px'}}>
                            ✓
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-1">Account Created</h6>
                          <p className="text-muted mb-0">Your trial account is active</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <div className="mr-3">
                          <div className={`rounded-circle d-flex align-items-center justify-content-center ${
                            paymentSetupStatus === 'complete' ? 'bg-success text-white' : 'bg-warning text-dark'
                          }`} style={{width: '30px', height: '30px'}}>
                            {paymentSetupStatus === 'complete' ? '✓' : '2'}
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-1">Payment Setup</h6>
                          <p className="text-muted mb-0">
                            {paymentSetupStatus === 'complete' ? 'Connected' : 'Required to receive payments'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Call to Action */}
                  {paymentSetupStatus !== 'complete' && (
                    <div className="alert alert-warning">
                      <h6 className="alert-heading">Payment Setup Required</h6>
                      <p className="mb-2">You must connect your bank account before completing any jobs.</p>
                      <p className="mb-0">This enables you to receive your job payments instantly.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Business Information Section */}
            <div className="card mb-4">
              <div className="card-header">
                <h4 className="mb-0">Business Information</h4>
              </div>
              <div className="card-body">
                {installer && (
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <strong>Business Name:</strong><br />
                      {installer.Installer_Name || 'Not set'}
                    </div>
                    <div className="col-md-6 mb-3">
                      <strong>Email:</strong><br />
                      {installer.installer_email || installer.Email || 'Not set'}
                    </div>
                    <div className="col-md-6 mb-3">
                      <strong>Phone:</strong><br />
                      {installer.installer_phone_number || 'Not set'}
                    </div>
                    <div className="col-md-6 mb-3">
                      <strong>Address:</strong><br />
                      {installer.installer_address_line1 ? (
                        <>
                          {installer.installer_address_line1}
                          {installer.installer_address_line2 && <><br />{installer.installer_address_line2}</>}
                          <br />
                          {installer.installer_address_city}, {installer.installer_state} {installer.installer_address_zip}
                        </>
                      ) : (
                        'Not set'
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Setup Section */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Payment Account</h4>
                {installer?.stripe_account_id && installer?.onboarding_complete && (
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={this.handleManagePayment}
                    disabled={this.state.paymentLoading}
                  >
                    {this.state.paymentLoading ? 'Loading...' : 'Manage Account'}
                  </button>
                )}
              </div>
              <div className="card-body">
                <PaymentSetup
                  installer={installer}
                  stripeCapabilities={this.state.stripeCapabilities}
                  onSetupComplete={this.handlePaymentSetupComplete}
                  hideManageButton={true}
                />
              </div>
            </div>

            {/* Calendar Connection Section */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Google Calendar</h4>
                {isCalendarConnected ? (
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={this.disconnectCalendar}
                    disabled={calendarLoading}
                  >
                    {calendarLoading ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                ) : (
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={this.connectCalendar}
                    disabled={calendarLoading}
                  >
                    {calendarLoading ? 'Connecting...' : 'Connect'}
                  </button>
                )}
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  {isCalendarConnected ? (
                    <>
                      <span className="badge badge-success mr-2">
                        <i className="fa fa-check mr-1"></i> Connected
                      </span>
                      <span className="text-muted">Jobs sync to your Google Calendar</span>
                    </>
                  ) : (
                    <>
                      <span className="badge badge-secondary mr-2">Not Connected</span>
                      <span className="text-muted">Connect to sync jobs to Google Calendar</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Location Settings Section */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Location Settings</h4>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={this.handleOpenSettingsModal}
                >
                  <i className="fa fa-edit mr-1"></i> Edit
                </button>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <strong>Materials Tracking:</strong><br />
                    <span className={`badge ${installer?.materials_tracking_enabled !== false ? 'badge-success' : 'badge-secondary'}`}>
                      {installer?.materials_tracking_enabled !== false ? 'Enabled' : 'Disabled'}
                    </span>
                    <p className="text-muted small mt-1 mb-0">
                      {installer?.materials_tracking_enabled !== false
                        ? 'Technicians will be prompted to record materials used when finishing services'
                        : 'Materials collection is disabled for this location'}
                    </p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Time Tracking:</strong><br />
                    <span className={`badge ${(installer?.time_tracking_mode && installer.time_tracking_mode !== 'none') ? 'badge-success' : 'badge-secondary'}`}>
                      {this.getTimeTrackingLabel(installer?.time_tracking_mode)}
                    </span>
                    <p className="text-muted small mt-1 mb-0">
                      {installer?.time_tracking_mode === 'required'
                        ? 'Staff must check in before starting work on jobs'
                        : installer?.time_tracking_mode === 'voluntary'
                        ? 'Staff can optionally check in/out to track hours'
                        : 'Time tracking features are hidden'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Operating Hours Section */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Operating Hours</h4>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={this.handleOpenOperatingHoursModal}
                  disabled={operatingHoursLoading}
                >
                  <i className="fa fa-edit mr-1"></i> Edit
                </button>
              </div>
              <div className="card-body">
                {operatingHoursLoading ? (
                  <div className="text-center">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className="operating-hours-grid">
                    {operatingHours.map((day, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <span className="font-weight-bold" style={{ width: '100px' }}>
                          {day.day_name || ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day.day_of_week]}
                        </span>
                        <span className={day.is_open ? 'text-success' : 'text-muted'}>
                          {day.is_open
                            ? `${formatTime(day.open_time)} - ${formatTime(day.close_time)}`
                            : 'Closed'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-muted small mt-3 mb-0">
                  <i className="fa fa-info-circle mr-1"></i>
                  Operating hours are used to filter available dates for scheduling and inform customers of your availability.
                </p>
              </div>
            </div>

          </div>

          {/* Right Column - Status & Quick Info */}
          <div className="col-md-4">
            
            {/* Account Status Card */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Account Status</h5>
              </div>
              <div className="card-body">
                {installer && (
                  <>
                    <div className="mb-3">
                      <strong>Account Type:</strong><br />
                      <span className={`badge ${installer.is_trial ? 'badge-warning' : 'badge-success'}`}>
                        {installer.is_trial ? 'Trial Specialist' : 'Active Specialist'}
                      </span>
                    </div>
                    
                    <div className="mb-3">
                      <strong>Subscription:</strong><br />
                      {installer.subscription || 'ASSIST'}
                    </div>
                    
                    <div className="mb-3">
                      <strong>Payment Status:</strong><br />
                      <span className={`badge ${installer.onboarding_complete ? 'badge-success' : 'badge-warning'}`}>
                        {installer.onboarding_complete ? 'Setup Complete' : 'Setup Required'}
                      </span>
                      {installer.onboarding_complete && this.state.stripeCapabilities &&
                        Object.keys(this.state.stripeCapabilities).length > 0 &&
                        this.state.stripeCapabilities.us_bank_account_ach_payments !== 'active' && (
                        <div className="mt-1">
                          <span className="badge badge-warning">
                            <i className="fa fa-exclamation-triangle mr-1"></i> Capabilities Pending
                          </span>
                        </div>
                      )}
                    </div>

                    {!!installer.is_trial ? (
                      <div className="mb-3">
                        <strong>Trial Jobs:</strong><br />
                        {installer.trial_jobs_completed || 0} completed
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            {/* Support Card */}
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Need Help?</h5>
              </div>
              <div className="card-body">
                <p>Having trouble with setup or have questions?</p>
                <a href="mailto:support@jobvious.com" className="btn btn-outline-primary btn-sm">
                  Contact Support
                </a>
              </div>
            </div>

          </div>
        </div>

        {/* Location Settings Modal */}
        <Modal show={showSettingsModal} onHide={this.handleCloseSettingsModal}>
          <Modal.Header closeButton>
            <Modal.Title>Location Settings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Materials Tracking */}
            <div className="form-group">
              <label className="font-weight-bold">Materials Tracking</label>
              <p className="text-muted small mb-2">
                When enabled, technicians will be prompted to record materials used when finishing applicable services.
              </p>
              <div className="custom-control custom-switch">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="materialsTrackingSwitch"
                  checked={settingsForm.materials_tracking_enabled}
                  onChange={(e) => this.handleSettingsChange('materials_tracking_enabled', e.target.checked)}
                />
                <label className="custom-control-label" htmlFor="materialsTrackingSwitch">
                  {settingsForm.materials_tracking_enabled ? 'Enabled' : 'Disabled'}
                </label>
              </div>
            </div>

            <hr />

            {/* Time Tracking */}
            <div className="form-group mb-0">
              <label className="font-weight-bold">Time Tracking</label>
              <p className="text-muted small mb-2">
                Track staff check-in/out times for attendance and payroll purposes.
              </p>
              {TIME_TRACKING_OPTIONS.map(option => (
                <div className="custom-control custom-radio mb-2" key={option.value}>
                  <input
                    type="radio"
                    className="custom-control-input"
                    id={`timeTracking_${option.value}`}
                    name="timeTrackingMode"
                    value={option.value}
                    checked={settingsForm.time_tracking_mode === option.value}
                    onChange={(e) => this.handleSettingsChange('time_tracking_mode', e.target.value)}
                  />
                  <label className="custom-control-label" htmlFor={`timeTracking_${option.value}`}>
                    <strong>{option.label}</strong>
                    <span className="text-muted ml-2">— {option.description}</span>
                  </label>
                </div>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseSettingsModal} disabled={settingsLoading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.handleSaveSettings} disabled={settingsLoading}>
              {settingsLoading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Calendar Selection Modal */}
        <Modal show={showCalendarSelection} onHide={this.handleCloseCalendarSelection}>
          <Modal.Header closeButton>
            <Modal.Title>Select Calendar</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">
              Choose which Google Calendar to sync jobs for this location:
            </p>
            <div className="list-group">
              {availableCalendars.map(calendar => (
                <button
                  key={calendar.id}
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                  onClick={() => this.handleCalendarSelection(calendar)}
                >
                  <div>
                    <strong>{calendar.name}</strong>
                    {calendar.primary && (
                      <span className="badge badge-info ml-2">Primary</span>
                    )}
                  </div>
                  <i className="fa fa-chevron-right text-muted"></i>
                </button>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseCalendarSelection}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Operating Hours Modal */}
        <Modal show={showOperatingHoursModal} onHide={this.handleCloseOperatingHoursModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Edit Operating Hours</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-4">
              Set your business hours for each day of the week. Closed days will not be available for scheduling.
            </p>
            <div className="operating-hours-form">
              {operatingHoursForm.map((day, index) => (
                <div key={index} className="form-row align-items-center py-3 border-bottom">
                  <div className="col-md-3">
                    <strong>{day.day_name || ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day.day_of_week]}</strong>
                  </div>
                  <div className="col-md-2">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id={`day-open-${index}`}
                        checked={day.is_open}
                        onChange={(e) => this.handleOperatingHoursChange(index, 'is_open', e.target.checked)}
                      />
                      <label className="custom-control-label" htmlFor={`day-open-${index}`}>
                        {day.is_open ? 'Open' : 'Closed'}
                      </label>
                    </div>
                  </div>
                  {day.is_open && (
                    <>
                      <div className="col-md-3">
                        <label className="sr-only">Open Time</label>
                        <input
                          type="time"
                          className="form-control"
                          value={day.open_time || '08:00'}
                          onChange={(e) => this.handleOperatingHoursChange(index, 'open_time', e.target.value)}
                        />
                      </div>
                      <div className="col-md-1 text-center">to</div>
                      <div className="col-md-3">
                        <label className="sr-only">Close Time</label>
                        <input
                          type="time"
                          className="form-control"
                          value={day.close_time || '17:00'}
                          onChange={(e) => this.handleOperatingHoursChange(index, 'close_time', e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseOperatingHoursModal} disabled={operatingHoursLoading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.handleSaveOperatingHours} disabled={operatingHoursLoading}>
              {operatingHoursLoading ? 'Saving...' : 'Save Hours'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export default ProfilePage;